import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Box, Button, TextField, MenuItem, Select, Grid, Backdrop, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OtpInput from 'react-otp-input';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import { toast } from 'react-toastify';
import { companyDetails } from 'utils/companyDetails';
import { mobileNumberReg } from 'utils/Regex';
import { getCountryList } from 'services/CustomerService';
import { requestAccountDeletionApi, verifyAccountDeletionApi } from 'services/DeleteAccountService';

const STEPS = { FORM: 'FORM', OTP: 'OTP' };

function ResendTimer({ duration, onResend }) {
  const [restartKey, setRestartKey] = useState(0);
  const [isResendActive, setIsResendActive] = useState(false);

  const handleResend = async () => {
    const ok = await onResend();
    if (ok) {
      setIsResendActive(false);
      setRestartKey((k) => k + 1);
    }
  };

  return (
    <Grid container direction="row" alignItems="center" justifyContent="center" spacing={2}>
      <Grid item>
        {!isResendActive && (
          <CountdownCircleTimer
            key={restartKey}
            isPlaying
            size={50}
            strokeWidth={5}
            isGrowing="true"
            rotation="clockwise"
            duration={duration}
            colors={['#0f538D']}
            onComplete={() => setIsResendActive(true)}
          >
            {({ remainingTime }) => (
              <div className="colorPrimary">
                {remainingTime} <span className="font7">Sec</span>
              </div>
            )}
          </CountdownCircleTimer>
        )}
      </Grid>
      <Grid item>
        <Button
          className={isResendActive ? 'otpResend colorPrimary' : 'otpResend colorGrey'}
          disabled={!isResendActive}
          sx={{ textDecoration: 'underline' }}
          onClick={handleResend}
        >
          Resend
        </Button>
      </Grid>
    </Grid>
  );
}

function DeleteAccount() {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({ mode: 'onBlur', defaultValues: { accountType: '', countryCode: '+91', mobile: '' } });

  const [countryList, setCountryList] = useState([]);
  const countryCode = watch('countryCode');

  const [step, setStep] = useState(STEPS.FORM);
  const [loading, setLoading] = useState(false);

  // Captured from the request step, used again on verify.
  const [request, setRequest] = useState({});
  const [authId, setAuthId] = useState('');
  const [otpSecret, setOtpSecret] = useState('');

  const [otp, setOtp] = useState('');
  const [otpKey, setOtpKey] = useState(0); // bump to remount OtpInput → cursor back to first box
  const [otpDuration, setOtpDuration] = useState(60);

  const resetOtpField = () => {
    setOtp('');
    setOtpKey((k) => k + 1);
  };

  // Country list for the dial-code dropdown
  useEffect(() => {
    (async () => {
      const response = await getCountryList();
      if (response?.data) {
        setCountryList(
          response.data
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((d) => ({ value: `+${d.dialCode}`, label: `${d.code} +${d.dialCode}` }))
        );
      }
    })();
  }, []);

  // ── Step 1: request deletion → send OTP ─────────────────────────────────────
  const onSubmit = async (data) => {
    const payload = {
      countryCode: data.countryCode,
      mobileNo: data.mobile,
      accountType: data.accountType,
    };
    setLoading(true);
    const res = await requestAccountDeletionApi(payload);
    setLoading(false);

    if (res?.success) {
      setRequest(payload);
      setAuthId(res.data?.authId);
      setOtpSecret(res.data?.otpSecret);
      setOtpDuration(res.data?.expiryTime || 60);
      resetOtpField();
      setStep(STEPS.OTP);
    } else {
      toast.error(res?.message || 'Something went wrong. Please try again.');
    }
  };

  const onResend = async () => {
    setLoading(true);
    const res = await requestAccountDeletionApi(request);
    setLoading(false);

    if (res?.success) {
      setAuthId(res.data?.authId);
      setOtpSecret(res.data?.otpSecret);
      setOtpDuration(res.data?.expiryTime || 60);
      resetOtpField();
      toast.success('OTP resent');
      return true;
    }
    toast.error(res?.message || 'Could not resend OTP.');
    return false;
  };

  // ── Step 2: verify OTP & delete ──────────────────────────────────────────────
  const onVerify = async (value) => {
    setLoading(true);
    const res = await verifyAccountDeletionApi({ authId, otpSecret, otp: value });
    setLoading(false);

    if (res?.success) {
      toast.success(res?.message || 'Account deleted successfully.');
      reset();
      setStep(STEPS.FORM);
      resetOtpField();
    } else {
      // Same as login: just reset the field + toast the API message, no red boxes.
      toast.error(res?.message || 'Invalid OTP. Please try again.');
      resetOtpField();
    }
  };

  const handleOtpChange = (value) => {
    setOtp(value);
    if (value.length === 4 && /^\d{4}$/.test(value)) {
      onVerify(value);
    }
  };

  const goBackToForm = () => {
    setStep(STEPS.FORM);
    resetOtpField();
  };

  // ── Renderers ───────────────────────────────────────────────────────────────
  const renderForm = () => (
    <>
      <div className="text-center">
        <h1 className="fontBold font36 colorBlue m0">
          Delete your <span className="colorYellow">account</span>
        </h1>
        <p className="colorGrey font16 fontNormal marginBottom35">
          Enter your registered mobile number to request account deletion.
        </p>
      </div>

      <Box sx={{ width: '100%', maxWidth: 360, mt: 4 }}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Controller
            name="accountType"
            control={control}
            rules={{ required: 'Please select an account type' }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Select account to delete"
                error={!!errors.accountType}
                helperText={errors.accountType?.message || ' '}
              >
                <MenuItem value="driver">Driver</MenuItem>
                <MenuItem value="customer">Customer</MenuItem>
              </TextField>
            )}
          />
          <Controller
            name="mobile"
            control={control}
            rules={{
              required: 'Mobile number is required',
              validate: (value) => {
                if (!/^\d+$/.test(value)) return 'Mobile number can contain digits only';
                if (value.length !== 10) return 'Mobile number must be 10 digits';
                if (!mobileNumberReg.test(value)) return 'Enter a valid mobile number';
                return true;
              },
            }}
            render={({ field }) => (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Select
                  value={countryCode}
                  onChange={(e) => setValue('countryCode', e.target.value)}
                  sx={{ minWidth: 110, height: 56 }}
                >
                  {countryList.map((country) => (
                    <MenuItem key={country.value} value={country.value}>
                      {country.label}
                    </MenuItem>
                  ))}
                </Select>
                <TextField
                  {...field}
                  fullWidth
                  label="Mobile number"
                  placeholder="Enter 10-digit mobile number"
                  error={!!errors.mobile}
                  helperText={errors.mobile?.message || ' '}
                  inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                />
              </Box>
            )}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" color="error" type="submit" sx={{ mt: 1 }}>
              Request account deletion
            </Button>
          </Box>
        </form>
      </Box>
    </>
  );

  const renderOtp = () => (
    <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
      <h1 className="fontBold font36 colorBlue m0">
        Enter your <span className="colorYellow">OTP</span>
      </h1>
      <p className="colorGrey font16 fontNormal m0">An authentication code has been sent to</p>
      <p className="colorGrey font16 fontMedium marginBottom35">
        {request.countryCode} {request.mobileNo}
      </p>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <OtpInput
          key={otpKey}
          containerStyle="otp"
          inputStyle="otpStyle"
          value={otp}
          onChange={handleOtpChange}
          numInputs={4}
          inputType="tel"
          shouldAutoFocus
          renderInput={(props) => <input {...props} disabled={loading} />}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <ResendTimer duration={otpDuration} onResend={onResend} />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', p: 2 }}>
      {/* ---Logo top-left--- */}
      <Box sx={{ position: 'absolute', top: 16, left: 24, display: 'flex', alignItems: 'center', gap: 1 }}>
        {step === STEPS.OTP && (
          <span onClick={goBackToForm} className="cursor" style={{ display: 'inline-flex' }}>
            <ArrowBackIcon className="colorBlue" />
          </span>
        )}
        <img src={companyDetails?.companyLogo} className="logoImg" alt="ITER logo" />
      </Box>

      {/* ---Content top-center--- */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 8 }}>
        {step === STEPS.FORM ? renderForm() : renderOtp()}
      </Box>

      <Backdrop className="backDropColor" sx={{ zIndex: 1300 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}

export default DeleteAccount;
